import {
  faAdd,
  faArrowRight,
  faCalendar,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import Charts from "../components/Charts";
import TopBarWidget from "../components/TopDashboardWidget";
import TableWrapper, { tableHeaderClass } from "../components/TableWrapper";
import { chaseHeadingData } from "../components/chaseListComponents/chaseListData";
import axiosInstance from "../utils/axiosInstance";
import ChaseRenderRow from '../components/chaseListComponents/chaseRenderRow'
import { useDebounceValue } from "usehooks-ts";
import formQueryUrl from "../utils/formQueryUrl";
import DateRangePicker from "../components/date-range-picker";
import { useRangePicker } from "../store/use-range-picker-model";
import UseGraph from "../utils/useGraph";
import ChatIcon from "../components/chat-components/chat-icon";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function Dashboard() {
  const navigate = useNavigate();
  const { setDates, startDate, endDate } = UseGraph()
  const [query, setQuery] = useDebounceValue("", 1000)
  const [chase, setChase] = useState<Record<string, any>[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const { onToggleRangePicker } = useRangePicker()

  useEffect(() => {
    const getChaseList = async () => {
      setLoading(true)
      const filtersArray = [{ search: query }]

      try {
        const res = await axiosInstance.get(`/chase?limit=${5}${formQueryUrl(filtersArray)}`)
        setChase(res.data.data)
        setTotalCount(res.data.totalCount)
        setLoading(false)
      } catch (error) {
        console.log(error)
        setLoading(false)
      }
    }

    getChaseList()
  }, [query])



  const handleClick = () => {
    navigate("/createChase");
  };

  const rangeLabel = startDate
    ? `${formatDate(startDate)}${endDate ? ` — ${formatDate(endDate)}` : ""}`
    : "All time";

  return (
    <PageLayout
      pageTitle="Dashboard"
      primaryAction={
        <div className="flex items-center gap-3 smallTablet:flex-wrap">
          <ChatIcon />
          <button
            onClick={handleClick}
            className="flex gap-2 text-[15px] font-dmSans font-[600] items-center px-5 py-3 smallTablet:px-4 smallTablet:py-2.5 smallTablet:text-[14px] text-white bg-gradient-to-r from-accent to-accent-bright shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm hover:-translate-y-0.5 transition-all duration-300 rounded-full"
          >
            <FontAwesomeIcon icon={faAdd} className="text-[13px]" />
            New Chase
          </button>
        </div>
      }
    >
      <div className="h-full flex flex-col gap-4">
        <div className="flex items-center gap-3 smallTablet:flex-wrap">
          <DateRangePicker onChange={(e) => setDates(e)}>
            <div
              onClick={onToggleRangePicker}
              className="flex items-center gap-2 h-10 px-4 rounded-full bg-surface border border-edge shadow-card text-[13px] font-medium text-light-gray hover:border-indigo-400/40 hover:text-text-primary transition-all duration-200 cursor-pointer select-none"
            >
              <FontAwesomeIcon icon={faCalendar} className="text-[12px] text-light-gray" />
              {rangeLabel}
            </div>
          </DateRangePicker>
        </div>

        <div className="flex flex-col gap-4">
          <TopBarWidget />
          <Charts />
        </div>

        <div className="bg-surface border border-edge rounded-2xl shadow-card px-6 pt-6 pb-2 animate-fade-in smallTablet:px-4 smallTablet:pt-4 smallTablet:min-w-0">
          <div className="flex w-full items-start justify-between gap-4 flex-wrap mb-5">
            <div className="min-w-0">
              <h1 className="text-[22px] font-bold text-text-primary leading-tight smallTablet:text-[18px]">Latest Chase</h1>
              <p className="mt-1 text-[13px] font-medium text-light-gray capitalize">
                All statuses
                <span className="normal-case"> · {new Intl.NumberFormat("en-GB").format(totalCount)} chases</span>
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap smallTablet:w-full">
              <div className="h-10 min-w-[290px] smallTablet:min-w-0 smallTablet:w-full rounded-full flex bg-input-bg items-center border border-input-border focus-within:border-indigo-400/40 focus-within:ring-2 focus-within:ring-indigo-400/20 transition-all duration-200 px-4">
                <FontAwesomeIcon icon={faSearch} className="text-[13px] text-light-gray mr-2.5 shrink-0" />
                <input
                  type="text"
                  placeholder="Search name, email or reference"
                  className="flex-grow bg-inherit text-[13px] text-text-primary placeholder:text-light-gray font-medium focus:outline-none smallTablet:min-w-0"
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>

              <button
                onClick={() => navigate("/chaselist")}
                className="flex items-center gap-2 h-10 px-4 rounded-full border border-edge text-[13px] font-medium text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200"
              >
                View all
                <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
              </button>
            </div>
          </div>

          <TableWrapper
            headingData={chaseHeadingData}
            totalCount={totalCount}
            isLoading={loading}
            headerClass={tableHeaderClass}
          >
            {
              chase?.map((item, index) => {
                return (
                  <ChaseRenderRow item={item} index={index} key={item.id} />
                )
              })
            }
          </TableWrapper>
        </div>
      </div>
    </PageLayout>
  );
}
